import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

const COLLECTION_MORPHEME_TABLE = "collection_morpheme";

/**
 * Matches ANY write to `collection_morpheme`, not just this morpheme's row.
 *
 * This used to be a per-morpheme predicate that resolved `event.rowId` back to a morpheme with a
 * SELECT. It was precise, and it was the single worst thing in the change-feed:
 *
 *   - the query key embeds `morpheme`, and registrations are PERMANENT by design (they must survive
 *     unmount — see `table-change-version.ts`), so the registry gains one entry per morpheme ever
 *     rendered. The star button in the morpheme toolbar registers it, so merely BROWSING grows it.
 *   - `table-change-version` dispatches EVERY committed write to EVERY registration.
 *   - that predicate was async and hit the database.
 *
 * Net cost: O(writes x morphemes-visited) SQL round-trips. It was the ONLY async/DB predicate among
 * all 27 `useTableChangeVersion` consumers; every other one is a synchronous `Set.has`. That is the
 * mechanism behind the post-rating stall in `word-sentence-drill`, which grew ~23s -> ~61s -> ~204s
 * across runs and finally blew the flow's 120s budget (dev run 31325947996). A stall that GROWS is
 * the signature of an unbounded registry scan, not of slow SQL.
 *
 * It was also WRONG on delete. The predicate read the row back by `rowid`; after a DELETE that row
 * is gone, so `changedRow?.morpheme === morpheme` is false and un-starring never invalidated its own
 * query. The precise predicate was buying accuracy it did not actually deliver.
 *
 * The trade: a write to any morpheme's collection row now invalidates every registered morpheme key,
 * so some queries refetch when their own data did not change. That refetch is a cheap indexed read
 * against a small table, and invalidation is coalesced per microtask
 * (`queue-coalesced-invalidations.ts`), so a burst of writes costs one round per key. Paying an
 * occasional extra SELECT beats paying N SELECTs on every single write.
 */
export function useGetCollectionMorphemeQueryKey(morpheme: string) {
  const collectionMorphemesQueryOptions = actorQueries(
    import.meta.location,
  ).collectionMorpheme.findMany.useQueryOptions({
    where: { morpheme },
  });
  // Built per call rather than hoisted to module scope: `tableNameMatcher` allocates a `Set`, and a
  // top-level call is a module side effect (`no-top-level-side-effects`). Every other
  // `useTableChangeVersion` consumer builds its matcher inline the same way.
  const withCollectionMorphemeVersion = useTableChangeVersion(
    tableNameMatcher([COLLECTION_MORPHEME_TABLE]),
    [COLLECTION_MORPHEME_TABLE],
  );

  return () =>
    withCollectionMorphemeVersion([
      "collectionMorpheme",
      morpheme,
      { collectionMorphemesQueryOptions },
    ] as const);
}

export const [useCollectionMorphemeQueryOptions, collectionMorphemeQueryFn] = createUseQueryOptions(
  useGetCollectionMorphemeQueryKey,
  ({ client, queryKey: [_, _morpheme, { collectionMorphemesQueryOptions }] }) => {
    return client.fetchQuery(collectionMorphemesQueryOptions);
  },
);

// Freshness comes from the `collection_morpheme` table matcher registered by the query-key getter
// above — table-scoped, deliberately not per-morpheme (see `collectionMorphemeMatcher`).
